import {
  BODY_SLOTS,
  PRIMARY_ATTRIBUTES,
  QUEST_EQUIPMENT_SLOTS,
  QUEST_SLOT_LABELS,
  ROLE_LABELS,
  SUPPORT_ROLES,
  SUPPORT_TARGET,
  aspectAffinity,
  aspectEffectsForCard,
  augmentTypeRules,
  buildDeckProfile,
  canUpgradeEquipmentCard,
  cardAttributes,
  cardHasType,
  cardRoles,
  cardSupportRoles,
  cardSynergyTypes,
  combatEntries,
  convertStacksDealsDamage,
  entryDealsDamage,
  fillsMissingEquipmentSlot,
  fillsMissingQuestSlot,
  flattenEffectEntries,
  formatLabel,
  resolveCardBoardSlot,
  resolveQuestSlot,
  scoreCardAgainstProfile,
  equipmentTier,
  findUnsupportedUpgrades,
} from "./deckSuggest";

const RECOMMEND_DECK_SIZE = 10;
const RECOMMEND_MAX_COPIES = 2;
const RECOMMEND_MAX_POWER = 10;
const RECOMMEND_MIN_SWAPS = 2;
const RECOMMEND_MAX_SWAPS = 4;
const SEARCH_TRIALS = 72;
const REMOVAL_POOL_PER_TYPE = 5;
const CANDIDATE_TOP = 6;
const SHORTLIST_PER_TYPE = 30;
const SHORTLIST_COMPLEMENTS = 6;
const NOVELTY_BONUS = 6;
const REPEAT_ADD_PENALTY = 4;
const PROPOSAL_BAND = 6;
const MAX_REASONS = 4;
const TYPE_SHARE_MIN = 3;
const QUEST_SHORTLIST_PER_SLOT = 3;
const QUEST_SLOT_CROWD = 4;
const SUPPORT_SHORTLIST_PER_TYPE = 6;
const EXPENSIVE_ABILITY_COST = 4;
const EXPENSIVE_ABILITY_MAX = 2;
const ABILITY_AVERAGE_COST_TARGET = 2.5;
const DECK_TYPES = ["equipment", "ability"];
const LINK_PASSIVE_IDS = new Set([
  "slottedActiveDamageAdd",
  "slottedAbilityCostReduce",
  "hone",
  "sharpen",
  "fletch",
]);
const PRIMARY_DAMAGE_ENTRY_IDS = new Set([
  "damageOpponent",
  "bleed",
  "poison",
  "convertPhysicalArmorToDamage",
  "convertMagicArmorToDamage",
]);
const RESOURCE_GRANT_IDS = new Set(["grantResources", "gainResources"]);
const SLOT_LABELS = { weapon: "Weapon", armor: "Armor", trinket: "Trinket" };

function toCatalogMap(catalog) {
  if (!catalog) return {};
  if (Array.isArray(catalog)) {
    const map = {};
    catalog.forEach((card) => {
      if (card && typeof card.id === "string") map[card.id] = card;
    });
    return map;
  }
  return catalog;
}

function isTokenCard(card) {
  return Boolean(card?.token) || cardHasType(card, "token");
}

function getCardPower(card) {
  const value = Number(card?.power);
  if (!Number.isFinite(value) || value < 0) return 0;
  return Math.floor(value);
}

function countIds(ids) {
  const counts = {};
  (ids || []).forEach((id) => {
    if (typeof id !== "string" || !id) return;
    counts[id] = (counts[id] || 0) + 1;
  });
  return counts;
}

function filledIds(ids) {
  return (ids || []).filter((id) => typeof id === "string" && id);
}

function ownershipCheck({ collection, isOwned } = {}) {
  if (typeof isOwned === "function") return (cardId) => Boolean(isOwned(cardId));
  return (cardId) => Boolean(cardId) && Number(collection?.[cardId]) > 0;
}

function clampRandom(random) {
  const value = Number(random());
  if (!Number.isFinite(value) || value <= 0) return 0;
  return value >= 1 ? 0.999999 : value;
}

function pickWeightedIndex(weights, random) {
  const total = weights.reduce((sum, weight) => sum + Math.max(0, weight), 0);
  if (!(total > 0)) return 0;
  let roll = clampRandom(random) * total;
  for (let index = 0; index < weights.length; index += 1) {
    roll -= Math.max(0, weights[index]);
    if (roll < 0) return index;
  }
  return weights.length - 1;
}

function shuffle(list, random) {
  const next = [...list];
  for (let index = next.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(clampRandom(random) * (index + 1));
    [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
  }
  return next;
}

function listNames(names) {
  const unique = [...new Set(names.filter(Boolean))];
  if (unique.length <= 1) return unique[0] || "";
  if (unique.length === 2) return `${unique[0]} and ${unique[1]}`;
  return `${unique[0]}, ${unique[1]} and ${unique.length - 2} more`;
}

function pluralize(count, singular, plural) {
  return `${count} ${count === 1 ? singular : plural}`;
}

function isDamagingEffect(effect) {
  return combatEntries(effect).some(entryDealsDamage);
}

function abilityDealsDamageType(ability, damageType) {
  const entry = combatEntries(ability?.effect).find(
    (candidate) =>
      candidate &&
      (PRIMARY_DAMAGE_ENTRY_IDS.has(candidate.id) ||
        convertStacksDealsDamage(candidate)),
  );
  if (!entry) return false;
  return (entry.damageType || "physical") === damageType;
}

function effectDealsPhysicalDamage(effect) {
  return combatEntries(effect).some(
    (entry) =>
      entryDealsDamage(entry) && (entry.damageType || "physical") === "physical",
  );
}

function effectHasEntry(effect, ids) {
  return combatEntries(effect).some((entry) => entry && ids.includes(entry.id));
}

function abilityAppliesStackAdd(ability, stack) {
  if (!stack) return false;
  return combatEntries(ability?.effect).some((entry) => {
    if (!entry) return false;
    if (stack === "poison" && entry.id === "poison") return true;
    if (stack === "bleed" && entry.id === "bleed") return true;
    if (
      (stack === "burn" || stack === "chill" || stack === "shock") &&
      (entry.id === stack ||
        (entry.id === "applyAilment" && (entry.ailment || entry.type) === stack) ||
        (entry[stack] || 0) > 0)
    ) {
      return true;
    }
    return entry.id === "convertStacks" && entry.stack === stack;
  });
}

function resolveAugmentModify(augment) {
  if (!augment) return {};
  if (Array.isArray(augment.modifyByLevel) && augment.modifyByLevel.length) {
    return { ...(augment.modifyByLevel[0] || {}) };
  }
  return { ...(augment.modify || {}) };
}

function augmentModifyApplies(modify, active) {
  if (!modify || !active) return false;
  const effect = active.effect;
  const damaging = isDamagingEffect(effect);
  if (modify.physicalDamageAdd && abilityDealsDamageType(active, "physical")) {
    return true;
  }
  if (modify.magicalDamageAdd && abilityDealsDamageType(active, "magical")) {
    return true;
  }
  if (modify.damageAdd && damaging) return true;
  if (
    (modify.fireDamageAdd || modify.coldDamageAdd || modify.lightningDamageAdd) &&
    damaging
  ) {
    return true;
  }
  for (const stack of ["burn", "chill", "shock"]) {
    if (
      modify[`${stack}Add`] &&
      (damaging || abilityAppliesStackAdd(active, stack))
    ) {
      return true;
    }
  }
  if (modify.bleedAdd && abilityAppliesStackAdd(active, "bleed")) return true;
  if (modify.poisonAdd && abilityAppliesStackAdd(active, "poison")) return true;
  if (modify.healAdd && effectHasEntry(effect, ["healSelf"])) return true;
  if (
    modify.physicalArmorAdd &&
    effectHasEntry(effect, ["temporaryPhysicalArmor"])
  ) {
    return true;
  }
  if (modify.magicalArmorAdd && effectHasEntry(effect, ["temporaryMagicArmor"])) {
    return true;
  }
  if (modify.minionHealthAdd && effect?.id === "summonMinion") return true;
  if (modify.ignoreArmor && damaging) return true;
  if (modify.echo) return true;
  const costMult = Number(modify.costMult);
  if (Number.isFinite(costMult) && costMult > 0 && costMult !== 1) return true;
  const outputMult = Number(modify.outputMult);
  if (Number.isFinite(outputMult) && outputMult > 0 && outputMult !== 1) {
    return true;
  }
  const costToHealth = Number(modify.costToHealth);
  if (Number.isFinite(costToHealth) && costToHealth > 0) return true;
  const levelAdd = Number(modify.levelAdd);
  if (Number.isFinite(levelAdd) && levelAdd !== 0) return true;
  if (effect?.id === "activateAura" || effect?.id === "summonMinion") {
    if (modify.costAdd !== undefined) return true;
    if (Number(modify.outputAdd) > 0) return true;
  }
  return false;
}

function augmentMatches(augmentCard, active) {
  const augment = augmentCard?.augment;
  if (!augment || !augment.match || !active) return false;
  const matchRule = augment.match;
  if (matchRule.kind && matchRule.kind !== active.kind) return false;
  if (matchRule.damaging && !isDamagingEffect(active.effect)) return false;
  const typesAny = Array.isArray(matchRule.typesAny)
    ? matchRule.typesAny.filter(Boolean)
    : [];
  if (typesAny.length && !typesAny.some((type) => cardHasType(active, type))) {
    return false;
  }
  const typeRules = augmentTypeRules(matchRule);
  if (typeRules.length && !typeRules.every((type) => cardHasType(active, type))) {
    return false;
  }
  if (matchRule.attribute && matchRule.attribute !== active.attribute) {
    return false;
  }
  return augmentModifyApplies(resolveAugmentModify(augment), active);
}

function passiveMatches(effect, ability) {
  if (!effect || !ability) return false;
  const matchRule = effect.match || {};
  if (matchRule.kind && ability.kind !== matchRule.kind) return false;
  if (matchRule.attribute && ability.attribute !== matchRule.attribute) {
    return false;
  }
  const typeRules = augmentTypeRules(matchRule);
  return !typeRules.length || typeRules.every((type) => cardHasType(ability, type));
}

function linkPassiveMatches(effect, ability) {
  if (!effect || !ability || !cardHasType(ability, "ability")) return false;
  if (
    effect.id === "slottedActiveDamageAdd" ||
    effect.id === "slottedAbilityCostReduce"
  ) {
    return passiveMatches(effect, ability);
  }
  if (ability.kind !== "active") return false;
  if (effect.id === "hone") {
    return cardHasType(ability, "melee") || effectDealsPhysicalDamage(ability.effect);
  }
  if (effect.id === "sharpen") {
    return cardHasType(ability, "melee") || cardHasType(ability, "physical");
  }
  if (effect.id === "fletch") return cardHasType(ability, "ranged");
  return false;
}

function equipmentLinkPassives(card) {
  if (!cardHasType(card, "equipment")) return [];
  return (card.passiveEffects || []).filter(
    (effect) => effect && LINK_PASSIVE_IDS.has(effect.id),
  );
}

function linkPhraseForAbility(effect, equipmentName) {
  if (effect.id === "slottedActiveDamageAdd") {
    return effect.cost
      ? `${equipmentName} can add damage while it is slotted`
      : `${equipmentName} adds damage while it is slotted`;
  }
  if (effect.id === "slottedAbilityCostReduce") {
    return `${equipmentName} lowers its cost while slotted`;
  }
  return `${equipmentName}'s ${formatLabel(effect.id)} boosts it`;
}

function linkPhraseForEquipment(effect, names) {
  if (effect.id === "slottedActiveDamageAdd") {
    return effect.cost
      ? `Can add damage to slotted ${names}`
      : `Adds damage to slotted ${names}`;
  }
  if (effect.id === "slottedAbilityCostReduce") {
    return `Lowers the cost of slotted ${names}`;
  }
  return `${formatLabel(effect.id)} boosts ${names}`;
}

function primaryCostAttributes(card) {
  if (!cardHasType(card, "ability")) return [];
  return PRIMARY_ATTRIBUTES.filter(
    (attribute) => Number(card.cost?.[attribute]) > 0,
  );
}

function abilityBaseCost(card) {
  if (!cardHasType(card, "ability") || card.kind !== "active") return 0;
  if (!card.cost || typeof card.cost !== "object") return 0;
  return Object.values(card.cost).reduce(
    (sum, value) =>
      typeof value === "number" && Number.isFinite(value) && value > 0
        ? sum + value
        : sum,
    0,
  );
}

function abilityCostStats(abilityCards, cache) {
  let expensive = 0;
  let total = 0;
  let count = 0;
  abilityCards.forEach((card) => {
    if (!card || card.kind !== "active") return;
    const traits = cache.traits(card);
    count += 1;
    total += traits.baseCost;
    if (traits.expensive) expensive += 1;
  });
  return { expensive, average: count ? total / count : 0 };
}

function suppliedAttributes(card) {
  if (!cardHasType(card, "equipment")) return [];
  const supplied = new Set(
    PRIMARY_ATTRIBUTES.filter(
      (attribute) => Number(card.resources?.[attribute]) > 0,
    ),
  );
  flattenEffectEntries(card.activatedEffect).forEach((entry) => {
    if (!RESOURCE_GRANT_IDS.has(entry.id)) return;
    if (entry.resources && typeof entry.resources === "object") {
      PRIMARY_ATTRIBUTES.forEach((attribute) => {
        if (Number(entry.resources[attribute]) > 0) supplied.add(attribute);
      });
    } else if (PRIMARY_ATTRIBUTES.includes(entry.attribute)) {
      supplied.add(entry.attribute);
    }
  });
  return PRIMARY_ATTRIBUTES.filter((attribute) => supplied.has(attribute));
}

function demandCounts(abilityCards) {
  const counts = {};
  abilityCards.forEach((card) => {
    primaryCostAttributes(card).forEach((attribute) => {
      counts[attribute] = (counts[attribute] || 0) + 1;
    });
  });
  return counts;
}

function createCardCache(aspect) {
  const affinities = new Map();
  const augmentPairs = new Map();
  const linkPairs = new Map();
  const traits = new Map();
  return {
    aspect,
    affinity(card) {
      if (!card) {
        return {
          benefits: [],
          penalties: [],
          sharedAttributes: [],
          synergy: false,
          compatible: true,
        };
      }
      if (!affinities.has(card.id)) {
        affinities.set(card.id, aspectAffinity(aspect, card));
      }
      return affinities.get(card.id);
    },
    aspectEffects(card) {
      return this.affinity(card);
    },
    traits(card) {
      if (!traits.has(card.id)) {
        const baseCost = abilityBaseCost(card);
        traits.set(card.id, {
          baseCost,
          expensive: baseCost >= EXPENSIVE_ABILITY_COST,
          costAttributes: primaryCostAttributes(card),
          suppliedAttributes: suppliedAttributes(card),
          linkPassives: equipmentLinkPassives(card),
          questSlot: resolveQuestSlot(card),
          roles: cardRoles(card),
          supportRoles: cardSupportRoles(card),
        });
      }
      return traits.get(card.id);
    },
    augmentMatches(augmentCard, active) {
      if (!augmentCard || !active || augmentCard === active) return false;
      const key = `${augmentCard.id}|${active.id}`;
      if (!augmentPairs.has(key)) {
        augmentPairs.set(
          key,
          augmentCard.kind === "augment" &&
            active.kind === "active" &&
            augmentMatches(augmentCard, active),
        );
      }
      return augmentPairs.get(key);
    },
    linkMatches(equipment, effectIndex, ability) {
      const key = `${equipment.id}#${effectIndex}|${ability.id}`;
      if (!linkPairs.has(key)) {
        const effect = this.traits(equipment).linkPassives[effectIndex];
        linkPairs.set(key, linkPassiveMatches(effect, ability));
      }
      return linkPairs.get(key);
    },
  };
}

function buildFitContext(equipmentCards, abilityCards, cache, getCard) {
  const supply = {};
  const demand = {};
  const linkEntries = [];
  equipmentCards.forEach((card) => {
    const traits = cache.traits(card);
    traits.suppliedAttributes.forEach((attribute) => {
      supply[attribute] = (supply[attribute] || 0) + 1;
    });
    traits.linkPassives.forEach((effect, effectIndex) => {
      linkEntries.push({ equipment: card, effect, effectIndex });
    });
  });
  abilityCards.forEach((card) => {
    cache.traits(card).costAttributes.forEach((attribute) => {
      demand[attribute] = (demand[attribute] || 0) + 1;
    });
  });
  const costStats = abilityCostStats(abilityCards, cache);
  return {
    profile: buildDeckProfile(equipmentCards, abilityCards, getCard, cache.aspect),
    cache,
    equipmentCards,
    abilityCards,
    actives: abilityCards.filter((card) => card.kind === "active"),
    augments: abilityCards.filter((card) => card.kind === "augment"),
    linkEntries,
    supply,
    demand,
    expensiveAbilities: costStats.expensive,
    averageAbilityCost: costStats.average,
  };
}

function evaluateDeck(equipmentCards, abilityCards, cache) {
  const allCards = [...equipmentCards, ...abilityCards];
  const attributeCounts = {};
  allCards.forEach((card) => {
    cardAttributes(card).forEach((attribute) => {
      if (!PRIMARY_ATTRIBUTES.includes(attribute)) return;
      attributeCounts[attribute] = (attributeCounts[attribute] || 0) + 1;
    });
  });
  cardAttributes(cache.aspect).forEach((attribute) => {
    if (!PRIMARY_ATTRIBUTES.includes(attribute)) return;
    attributeCounts[attribute] = (attributeCounts[attribute] || 0) + 3;
  });
  const attributeTotal = Object.values(attributeCounts).reduce(
    (sum, count) => sum + count,
    0,
  );
  const attributeScore = attributeTotal
    ? Object.values(attributeCounts).reduce((sum, count) => sum + count * count, 0) /
      attributeTotal
    : 0;

  const typeCounts = {};
  allCards.forEach((card) => {
    cardSynergyTypes(card).forEach((type) => {
      typeCounts[type] = (typeCounts[type] || 0) + 1;
    });
  });
  const typeScore = Object.values(typeCounts).reduce(
    (sum, count) => sum + (count >= 2 ? (count - 1) * 0.5 : 0),
    0,
  );

  let benefitCards = 0;
  let penaltyCards = 0;
  let synergyCards = 0;
  let offAspectCards = 0;
  allCards.forEach((card) => {
    const affinity = cache.affinity(card);
    if (affinity.benefits.length) benefitCards += 1;
    if (affinity.penalties.length) penaltyCards += 1;
    if (affinity.synergy) synergyCards += 1;
    if (!affinity.compatible && !affinity.penalties.length) offAspectCards += 1;
  });

  const supportCounts = { equipment: 0, ability: 0 };
  const roleCounts = {};
  equipmentCards.forEach((card) => {
    if (cache.traits(card).supportRoles.length) supportCounts.equipment += 1;
  });
  abilityCards.forEach((card) => {
    if (cache.traits(card).supportRoles.length) supportCounts.ability += 1;
  });
  allCards.forEach((card) => {
    cache.traits(card).roles.forEach((role) => {
      roleCounts[role] = (roleCounts[role] || 0) + 1;
    });
  });
  const supportShortfall = DECK_TYPES.reduce(
    (sum, deckType) => sum + Math.max(0, SUPPORT_TARGET - supportCounts[deckType]),
    0,
  );
  const supportVariety = SUPPORT_ROLES.filter((role) => roleCounts[role] > 0).length;

  const supply = {};
  equipmentCards.forEach((card) => {
    cache.traits(card).suppliedAttributes.forEach((attribute) => {
      supply[attribute] = (supply[attribute] || 0) + 1;
    });
  });
  let unsupported = 0;
  let thin = 0;
  abilityCards.forEach((card) => {
    const attributes = cache.traits(card).costAttributes;
    if (!attributes.length) return;
    const minimum = Math.min(...attributes.map((attribute) => supply[attribute] || 0));
    if (minimum === 0) unsupported += 1;
    else if (minimum === 1) thin += 1;
  });

  const actives = abilityCards.filter((card) => card.kind === "active");
  const costStats = abilityCostStats(abilityCards, cache);
  const excessExpensive = Math.max(0, costStats.expensive - EXPENSIVE_ABILITY_MAX);
  const averageCostExcess = Math.max(
    0,
    costStats.average - ABILITY_AVERAGE_COST_TARGET,
  );
  let augmentScore = 0;
  abilityCards.forEach((card) => {
    if (card.kind !== "augment") return;
    const matches = actives.filter((active) =>
      cache.augmentMatches(card, active),
    ).length;
    augmentScore += matches ? 1.5 * Math.min(matches, 4) : -6;
  });

  let linkScore = 0;
  equipmentCards.forEach((card) => {
    cache.traits(card).linkPassives.forEach((_, effectIndex) => {
      const matches = actives.filter((active) =>
        cache.linkMatches(card, effectIndex, active),
      ).length;
      linkScore += matches ? Math.min(matches, 4) : -2;
    });
  });

  // Tier 2+ equipment needs a lower-tier base of its item type in the deck.
  const unsupportedUpgrades = findUnsupportedUpgrades(equipmentCards).length;

  const slotCounts = {};
  equipmentCards.forEach((card) => {
    const boardSlot = resolveCardBoardSlot(card);
    if (boardSlot) slotCounts[boardSlot] = (slotCounts[boardSlot] || 0) + 1;
  });
  const coveredSlots = BODY_SLOTS.filter((slot) => slotCounts[slot] > 0);
  const missingSlots = BODY_SLOTS.length - coveredSlots.length;
  const questSlotCounts = {};
  equipmentCards.forEach((card) => {
    const questSlot = cache.traits(card).questSlot;
    if (questSlot) questSlotCounts[questSlot] = (questSlotCounts[questSlot] || 0) + 1;
  });
  const coveredQuestSlots = QUEST_EQUIPMENT_SLOTS.filter(
    (slot) => questSlotCounts[slot] > 0,
  );
  const missingQuestSlots = QUEST_EQUIPMENT_SLOTS.length - coveredQuestSlots.length;
  const crowdedQuestSlots = Object.values(questSlotCounts).reduce(
    (sum, count) => sum + Math.max(0, count - QUEST_SLOT_CROWD),
    0,
  );

  return {
    score:
      attributeScore +
      typeScore +
      2.5 * benefitCards -
      6 * penaltyCards +
      synergyCards -
      4 * offAspectCards -
      6 * unsupported -
      1.5 * thin +
      augmentScore +
      linkScore -
      15 * missingSlots -
      10 * missingQuestSlots -
      3 * crowdedQuestSlots -
      5 * supportShortfall +
      1.5 * Math.min(supportVariety, 4) -
      8 * excessExpensive -
      4 * averageCostExcess -
      12 * unsupportedUpgrades,
    unsupportedUpgrades,
    expensiveAbilities: costStats.expensive,
    averageAbilityCost: costStats.average,
    coveredSlots,
    coveredQuestSlots,
    questSlotCounts,
    supportCounts,
    roleCounts,
    unsupported,
    supply,
  };
}

function scoreCardFit(card, deckType, context) {
  const { profile, cache, actives, augments, linkEntries, supply, demand } =
    context;
  const affinity = cache.affinity(card);
  const traits = cache.traits(card);
  let score = scoreCardAgainstProfile(card, profile, deckType);
  if (deckType === "ability") {
    if (card.kind === "augment") {
      const matches = actives.filter((active) =>
        cache.augmentMatches(card, active),
      ).length;
      score += matches ? 4 + 2 * Math.min(matches, 3) : -12;
    }
    if (card.kind === "active") {
      const augmentMatches = augments.filter((entry) =>
        cache.augmentMatches(entry, card),
      ).length;
      score += 4 * Math.min(augmentMatches, 2);
      const links = linkEntries.filter((entry) =>
        cache.linkMatches(entry.equipment, entry.effectIndex, card),
      ).length;
      score += 3 * Math.min(links, 3);
    }
    if (traits.costAttributes.some((attribute) => !(supply[attribute] > 0))) {
      score -= 10;
    }
    if (traits.expensive && context.expensiveAbilities >= EXPENSIVE_ABILITY_MAX) {
      score -= 14;
    } else if (
      traits.baseCost > ABILITY_AVERAGE_COST_TARGET &&
      context.averageAbilityCost > ABILITY_AVERAGE_COST_TARGET
    ) {
      score -= 2 * (traits.baseCost - ABILITY_AVERAGE_COST_TARGET);
    }
  } else {
    traits.linkPassives.forEach((_, effectIndex) => {
      const matches = actives.filter((active) =>
        cache.linkMatches(card, effectIndex, active),
      ).length;
      score += matches ? 2 * Math.min(matches, 3) : -4;
    });
    traits.suppliedAttributes.forEach((attribute) => {
      const needed = demand[attribute] || 0;
      score += Math.min(needed, 6) * 0.5;
      if (needed > 0 && !(supply[attribute] > 0)) score += 8;
    });
  }
  return { score, incompatible: !affinity.compatible };
}

function strongestShare(entries) {
  return entries
    .filter((entry) => entry.count >= TYPE_SHARE_MIN)
    .sort((a, b) => b.count - a.count || a.key.localeCompare(b.key))[0];
}

function describeLinks(card, deckType, cache, partnerEquipment, partnerAbility) {
  const traits = cache.traits(card);
  const partnerActives = partnerAbility.filter((entry) => entry.kind === "active");
  const linkReasons = [];
  if (deckType === "ability" && card.kind === "augment") {
    const matched = partnerActives.filter((active) =>
      cache.augmentMatches(card, active),
    );
    if (matched.length) {
      linkReasons.push(`Augments ${listNames(matched.map((entry) => entry.name))}`);
    }
  }
  if (deckType === "ability" && card.kind === "active") {
    const augments = partnerAbility.filter((entry) =>
      cache.augmentMatches(entry, card),
    );
    if (augments.length) {
      linkReasons.push(
        `${listNames(augments.map((entry) => entry.name))} can augment it`,
      );
    }
    for (const equipment of partnerEquipment) {
      const effect = cache
        .traits(equipment)
        .linkPassives.find((entry) => linkPassiveMatches(entry, card));
      if (effect) {
        linkReasons.push(linkPhraseForAbility(effect, equipment.name));
        break;
      }
    }
  }
  if (deckType === "equipment") {
    traits.linkPassives.forEach((effect) => {
      const matched = partnerActives.filter((active) =>
        linkPassiveMatches(effect, active),
      );
      if (matched.length) {
        linkReasons.push(
          linkPhraseForEquipment(effect, listNames(matched.map((entry) => entry.name))),
        );
      }
    });
    const demand = demandCounts(partnerAbility);
    const supplied = traits.suppliedAttributes
      .map((attribute) => ({ attribute, needed: demand[attribute] || 0 }))
      .filter((entry) => entry.needed > 0)
      .sort((a, b) => b.needed - a.needed);
    if (supplied.length) {
      linkReasons.push(
        `Supplies ${formatLabel(supplied[0].attribute)} for ${pluralize(
          supplied[0].needed,
          "ability",
          "abilities",
        )}`,
      );
    }
  }
  if (deckType === "ability" && traits.costAttributes.length) {
    const supply = {};
    partnerEquipment.forEach((equipment) => {
      cache.traits(equipment).suppliedAttributes.forEach((attribute) => {
        supply[attribute] = (supply[attribute] || 0) + 1;
      });
    });
    const backing = Math.min(
      ...traits.costAttributes.map((attribute) => supply[attribute] || 0),
    );
    if (backing > 0) {
      linkReasons.push(
        `${traits.costAttributes.map(formatLabel).join("/")} cost backed by ${backing} equipment`,
      );
    }
  }
  const partners = [...partnerEquipment, ...partnerAbility];
  const sharedType = strongestShare(
    cardSynergyTypes(card).map((type) => ({
      key: type,
      count: partners.filter((entry) => cardHasType(entry, type)).length,
    })),
  );
  if (sharedType) {
    linkReasons.push(`Shares ${formatLabel(sharedType.key)} with ${sharedType.count} cards`);
  }
  const sharedAttribute = strongestShare(
    cardAttributes(card).map((attribute) => ({
      key: attribute,
      count: partners.filter((entry) => cardAttributes(entry).includes(attribute))
        .length,
    })),
  );
  if (sharedAttribute) {
    linkReasons.push(
      `Shares ${formatLabel(sharedAttribute.key)} with ${sharedAttribute.count} cards`,
    );
  }
  return linkReasons;
}

function describeAddedCard(card, deckType, slotIndex, context) {
  const {
    cache,
    keptEquipment,
    keptAbility,
    equipmentCards,
    abilityCards,
    missingSlots,
    missingQuestSlots,
    roleCounts,
    supportCounts,
  } = context;
  const affinity = cache.affinity(card);
  const traits = cache.traits(card);

  const aspectReasons = [...affinity.benefits];
  if (!aspectReasons.length && affinity.sharedAttributes.length && cache.aspect) {
    aspectReasons.push(
      `Matches ${cache.aspect.name || "the Aspect"}'s ${affinity.sharedAttributes
        .map(formatLabel)
        .join("/")}`,
    );
  }

  const coverageReasons = [];
  if (deckType === "equipment") {
    const boardSlot = resolveCardBoardSlot(card);
    if (traits.questSlot && missingQuestSlots.includes(traits.questSlot)) {
      coverageReasons.push(
        `Covers ${QUEST_SLOT_LABELS[traits.questSlot]} for the equip-every-slot Quest`,
      );
    } else if (boardSlot && missingSlots.includes(boardSlot)) {
      coverageReasons.push(`Covers the empty ${SLOT_LABELS[boardSlot]} slot`);
    }
  }

  const roleReasons = [];
  const lackingRoles = traits.supportRoles.filter((role) => !(roleCounts[role] > 0));
  if (lackingRoles.length) {
    roleReasons.push(
      `Adds ${listNames(lackingRoles.map((role) => ROLE_LABELS[role]))}, which the deck lacks`,
    );
  } else if (
    traits.supportRoles.length &&
    (supportCounts[deckType] || 0) < SUPPORT_TARGET
  ) {
    roleReasons.push(
      `Adds ${listNames(
        traits.supportRoles.map((role) => ROLE_LABELS[role]),
      )} so the deck isn't only damage`,
    );
  }

  const keptLinks = describeLinks(card, deckType, cache, keptEquipment, keptAbility);
  const otherEquipment = equipmentCards.filter(
    (_, index) => deckType !== "equipment" || index !== slotIndex,
  );
  const otherAbility = abilityCards.filter(
    (_, index) => deckType !== "ability" || index !== slotIndex,
  );
  const linkReasons = describeLinks(card, deckType, cache, otherEquipment, otherAbility);
  const upgradeReasons = [];
  let upgradeLinked = false;
  if (deckType === "equipment") {
    const base = otherEquipment.find((entry) => canUpgradeEquipmentCard(card, entry));
    if (base) {
      upgradeReasons.push(`Upgrades your ${base.name} (Tier ${equipmentTier(base)})`);
      upgradeLinked ||= keptEquipment.includes(base);
    }
    const upgrade = otherEquipment.find((entry) => canUpgradeEquipmentCard(entry, card));
    if (upgrade) {
      upgradeReasons.push(`Gives ${upgrade.name} a Tier ${equipmentTier(card)} base to upgrade`);
      upgradeLinked ||= keptEquipment.includes(upgrade);
    }
  }
  const augmentMatched =
    card.kind !== "augment" ||
    otherAbility.some(
      (active) => active.kind === "active" && cache.augmentMatches(card, active),
    );

  return {
    reasons: [
      ...aspectReasons.slice(0, 1),
      ...coverageReasons,
      ...upgradeReasons,
      ...roleReasons,
      ...linkReasons,
      ...aspectReasons.slice(1),
    ],
    linked: (keptLinks.length > 0 || upgradeLinked) && augmentMatched,
  };
}

function describeRemovedCard(card, deckType, context) {
  const {
    cache,
    equipmentCards,
    abilityCards,
    supply,
    supportCounts,
    expensiveAbilities,
  } = context;
  if (
    deckType === "equipment" &&
    findUnsupportedUpgrades(equipmentCards || []).includes(card)
  ) {
    return `No Tier ${equipmentTier(card) - 1} base to upgrade from, so it can only be invoked`;
  }
  const affinity = cache.affinity(card);
  const penalty = affinity.penalties[0];
  if (penalty) return penalty;
  if (!affinity.compatible && cache.aspect) {
    return `Doesn't match ${cache.aspect.name || "the Aspect"}'s ${cardAttributes(
      cache.aspect,
    )
      .map(formatLabel)
      .join("/")}`;
  }
  if (deckType === "ability" && card.kind === "augment") {
    const actives = abilityCards.filter((entry) => entry.kind === "active");
    if (!actives.some((active) => cache.augmentMatches(card, active))) {
      return "No active ability here it can augment";
    }
  }
  if (deckType === "ability") {
    const missing = primaryCostAttributes(card).filter(
      (attribute) => !(supply[attribute] > 0),
    );
    if (missing.length) {
      return `No equipment supplies its ${missing.map(formatLabel).join("/")} cost`;
    }
    if (
      cache.traits(card).expensive &&
      (expensiveAbilities || 0) > EXPENSIVE_ABILITY_MAX
    ) {
      return `${expensiveAbilities} abilities cost ${EXPENSIVE_ABILITY_COST}+; keep only 1-2 so the deck stays castable`;
    }
  }
  if (deckType === "equipment") {
    const actives = abilityCards.filter((entry) => entry.kind === "active");
    const idle = equipmentLinkPassives(card).find(
      (effect) => !actives.some((active) => linkPassiveMatches(effect, active)),
    );
    if (idle) return `${formatLabel(idle.id)} has no matching active ability`;
  }
  const traits = cache.traits(card);
  if (
    traits.roles.includes("damage") &&
    !traits.supportRoles.length &&
    (supportCounts?.[deckType] || 0) < SUPPORT_TARGET
  ) {
    return "Damage only, and the deck needs more healing, defense or ailments";
  }
  return "Fewest links to this deck's attributes and types";
}

function deckPower(equipmentIds, abilityIds, aspectId, catalogMap) {
  return [...filledIds(equipmentIds), ...filledIds(abilityIds), aspectId]
    .filter(Boolean)
    .reduce((sum, cardId) => sum + getCardPower(catalogMap[cardId]), 0);
}

function countDeckCardChanges(before = {}, after = {}) {
  return DECK_TYPES.reduce((total, deckType) => {
    const beforeCounts = countIds(before[deckType]);
    const afterCounts = countIds(after[deckType]);
    return (
      total +
      Object.entries(beforeCounts).reduce(
        (sum, [cardId, count]) => sum + Math.max(0, count - (afterCounts[cardId] || 0)),
        0,
      )
    );
  }, 0);
}

function proposalSignature(removedIds, addedIds) {
  return `${[...(removedIds || [])].sort().join(",")}>${[...(addedIds || [])]
    .sort()
    .join(",")}`;
}

function validateRecommendedDeck({
  equipmentIds,
  abilityIds,
  aspectId,
  expectedAspectId,
  catalog,
  collection,
  isOwned,
  maxPower = RECOMMEND_MAX_POWER,
} = {}) {
  const catalogMap = toCatalogMap(catalog);
  const owns = ownershipCheck({ collection, isOwned });
  const fail = (message) => ({ ok: false, message });
  if (typeof aspectId !== "string" || !aspectId) return fail("Choose an Aspect.");
  if (expectedAspectId !== undefined && aspectId !== expectedAspectId) {
    return fail("The Aspect must stay the same.");
  }
  const aspect = catalogMap[aspectId];
  if (!aspect || !cardHasType(aspect, "aspect")) return fail("Choose a valid Aspect.");
  if (!owns(aspectId)) return fail(`You don't own ${aspect.name || aspectId}.`);
  const sides = [
    ["equipment", equipmentIds, "Equipment"],
    ["ability", abilityIds, "Ability"],
  ];
  for (const [deckType, ids, label] of sides) {
    if (
      !Array.isArray(ids) ||
      ids.length !== RECOMMEND_DECK_SIZE ||
      ids.some((cardId) => typeof cardId !== "string" || !cardId)
    ) {
      return fail(`${label} needs exactly ${RECOMMEND_DECK_SIZE} cards.`);
    }
    const counts = countIds(ids);
    for (const [cardId, count] of Object.entries(counts)) {
      const card = catalogMap[cardId];
      if (!card || !cardHasType(card, deckType)) {
        return fail(`${label} has a card that isn't in the catalog.`);
      }
      if (isTokenCard(card)) return fail(`${card.name} is a token card.`);
      if (!owns(cardId)) return fail(`You don't own ${card.name}.`);
      if (count > RECOMMEND_MAX_COPIES) {
        return fail(`Max ${RECOMMEND_MAX_COPIES} copies of ${card.name}.`);
      }
    }
  }
  const power = deckPower(equipmentIds, abilityIds, aspectId, catalogMap);
  if (power > maxPower) return fail(`Total Power is ${power}/${maxPower}.`);
  return { ok: true, power };
}

function getDeckRecommendBlocker({
  equipment,
  ability,
  aspectId,
  catalog,
  collection,
  isOwned,
  maxPower = RECOMMEND_MAX_POWER,
} = {}) {
  if (typeof aspectId !== "string" || !aspectId) return "Choose an Aspect.";
  const equipmentIds = filledIds(equipment);
  const abilityIds = filledIds(ability);
  if (equipmentIds.length !== RECOMMEND_DECK_SIZE) {
    return `Fill all ${RECOMMEND_DECK_SIZE} Equipment slots (${equipmentIds.length}/${RECOMMEND_DECK_SIZE}).`;
  }
  if (abilityIds.length !== RECOMMEND_DECK_SIZE) {
    return `Fill all ${RECOMMEND_DECK_SIZE} Ability slots (${abilityIds.length}/${RECOMMEND_DECK_SIZE}).`;
  }
  const validation = validateRecommendedDeck({
    equipmentIds,
    abilityIds,
    aspectId,
    catalog,
    collection,
    isOwned,
    maxPower,
  });
  return validation.ok ? null : validation.message;
}

function buildRemovalPool(state) {
  const { baseline, getCard, cache, baseEvaluation } = state;
  const pool = [];
  const slotCounts = {};
  baseline.equipment.forEach((cardId) => {
    const boardSlot = resolveCardBoardSlot(getCard(cardId));
    if (boardSlot) slotCounts[boardSlot] = (slotCounts[boardSlot] || 0) + 1;
  });
  // Stranded Tier 2+ equipment (no base to upgrade from) is removed first,
  // even when it is the only card covering its slot; proposals must still
  // re-cover the slot.
  const stranded = new Set(
    findUnsupportedUpgrades(baseline.equipment.map(getCard)).map((card) => card.id),
  );
  DECK_TYPES.forEach((deckType) => {
    const entries = [];
    const trimExpensive =
      deckType === "ability" &&
      baseEvaluation.expensiveAbilities > EXPENSIVE_ABILITY_MAX;
    baseline[deckType].forEach((cardId, index) => {
      const card = getCard(cardId);
      const traits = cache.traits(card);
      const strandedUpgrade = deckType === "equipment" && stranded.has(cardId);
      if (deckType === "equipment" && !strandedUpgrade) {
        const boardSlot = resolveCardBoardSlot(card);
        if (boardSlot && slotCounts[boardSlot] <= 1) return;
        if (
          traits.questSlot &&
          (baseEvaluation.questSlotCounts[traits.questSlot] || 0) <= 1
        ) {
          return;
        }
      }
      const remaining = {
        equipment: baseline.equipment.filter(
          (_, slotIndex) => deckType !== "equipment" || slotIndex !== index,
        ),
        ability: baseline.ability.filter(
          (_, slotIndex) => deckType !== "ability" || slotIndex !== index,
        ),
      };
      const fit = scoreCardFit(
        card,
        deckType,
        buildFitContext(
          remaining.equipment.map(getCard),
          remaining.ability.map(getCard),
          cache,
          getCard,
        ),
      );
      entries.push({
        deckType,
        index,
        cardId,
        fit: fit.score,
        trim: (trimExpensive && traits.expensive) || strandedUpgrade,
      });
    });
    entries
      .sort(
        (a, b) =>
          Number(b.trim) - Number(a.trim) || a.fit - b.fit || a.index - b.index,
      )
      .slice(0, REMOVAL_POOL_PER_TYPE)
      .forEach((entry, rank) => {
        pool.push({ ...entry, weight: REMOVAL_POOL_PER_TYPE - rank });
      });
  });
  return pool;
}

function assessProposal(state, working, chosen) {
  const {
    baseline,
    getCard,
    cache,
    baseEvaluation,
    baselineCounts,
    aspectId,
    catalogMap,
    owns,
    maxPower,
    baselineContext,
  } = state;
  const validation = validateRecommendedDeck({
    equipmentIds: working.equipment,
    abilityIds: working.ability,
    aspectId,
    expectedAspectId: aspectId,
    catalog: catalogMap,
    isOwned: owns,
    maxPower,
  });
  if (!validation.ok) return null;
  const changes = countDeckCardChanges(baseline, working);
  if (
    changes !== chosen.length ||
    changes < RECOMMEND_MIN_SWAPS ||
    changes > RECOMMEND_MAX_SWAPS
  ) {
    return null;
  }
  const equipmentCards = working.equipment.map(getCard);
  const abilityCards = working.ability.map(getCard);
  const evaluation = evaluateDeck(equipmentCards, abilityCards, cache);
  if (
    baseEvaluation.coveredSlots.some(
      (slot) => !evaluation.coveredSlots.includes(slot),
    )
  ) {
    return null;
  }
  if (
    baseEvaluation.coveredQuestSlots.some(
      (slot) => !evaluation.coveredQuestSlots.includes(slot),
    )
  ) {
    return null;
  }
  if (
    DECK_TYPES.some(
      (deckType) =>
        evaluation.supportCounts[deckType] <
        Math.min(baseEvaluation.supportCounts[deckType], SUPPORT_TARGET),
    )
  ) {
    return null;
  }
  if (
    SUPPORT_ROLES.some(
      (role) => baseEvaluation.roleCounts[role] > 0 && !(evaluation.roleCounts[role] > 0),
    )
  ) {
    return null;
  }
  if (evaluation.unsupported > baseEvaluation.unsupported) return null;
  if (evaluation.unsupportedUpgrades > baseEvaluation.unsupportedUpgrades) {
    return null;
  }
  if (
    evaluation.expensiveAbilities >
    Math.max(baseEvaluation.expensiveAbilities, EXPENSIVE_ABILITY_MAX)
  ) {
    return null;
  }
  if (
    evaluation.averageAbilityCost >
    Math.max(baseEvaluation.averageAbilityCost, ABILITY_AVERAGE_COST_TARGET)
  ) {
    return null;
  }
  const delta = evaluation.score - baseEvaluation.score;
  if (delta < 0) return null;

  const swappedSlots = new Set(chosen.map((slot) => `${slot.deckType}:${slot.index}`));
  const keptEquipment = working.equipment
    .filter((_, index) => !swappedSlots.has(`equipment:${index}`))
    .map(getCard);
  const keptAbility = working.ability
    .filter((_, index) => !swappedSlots.has(`ability:${index}`))
    .map(getCard);
  const roleCounts = {};
  [...keptEquipment, ...keptAbility].forEach((card) => {
    cache.traits(card).roles.forEach((role) => {
      roleCounts[role] = (roleCounts[role] || 0) + 1;
    });
  });
  const context = {
    cache,
    keptEquipment,
    keptAbility,
    equipmentCards,
    abilityCards,
    roleCounts,
    supportCounts: {
      equipment: keptEquipment.filter((card) => cache.traits(card).supportRoles.length)
        .length,
      ability: keptAbility.filter((card) => cache.traits(card).supportRoles.length)
        .length,
    },
    missingSlots: BODY_SLOTS.filter(
      (slot) => !baseEvaluation.coveredSlots.includes(slot),
    ),
    missingQuestSlots: QUEST_EQUIPMENT_SLOTS.filter(
      (slot) => !baseEvaluation.coveredQuestSlots.includes(slot),
    ),
  };
  const swaps = [];
  for (const slot of chosen) {
    const added = getCard(working[slot.deckType][slot.index]);
    if (!cache.affinity(added).compatible) return null;
    const description = describeAddedCard(added, slot.deckType, slot.index, context);
    if (!description.linked || !description.reasons.length) return null;
    swaps.push({
      deckType: slot.deckType,
      index: slot.index,
      removedId: slot.cardId,
      addedId: added.id,
      reasons: description.reasons.slice(0, MAX_REASONS),
      removedReason: describeRemovedCard(
        getCard(slot.cardId),
        slot.deckType,
        baselineContext,
      ),
    });
  }
  swaps.sort(
    (a, b) =>
      DECK_TYPES.indexOf(a.deckType) - DECK_TYPES.indexOf(b.deckType) ||
      a.index - b.index,
  );
  const addedIds = swaps.map((swap) => swap.addedId);
  const novelty = new Set(addedIds.filter((cardId) => !baselineCounts[cardId]))
    .size;
  return {
    equipmentIds: [...working.equipment],
    abilityIds: [...working.ability],
    swaps,
    signature: proposalSignature(
      swaps.map((swap) => swap.removedId),
      addedIds,
    ),
    power: validation.power,
    rank: delta + 1.5 * novelty,
  };
}

function buildShortlist(eligible, context, baselineCounts) {
  const { cache } = context;
  const ranked = {};
  DECK_TYPES.forEach((deckType) => {
    ranked[deckType] = eligible[deckType]
      .map((card) => ({
        card,
        score:
          scoreCardFit(card, deckType, context).score +
          (baselineCounts[card.id] ? 0 : NOVELTY_BONUS),
      }))
      .sort((a, b) => b.score - a.score || a.card.id.localeCompare(b.card.id))
      .map((entry) => entry.card);
  });
  const included = new Set();
  const shortlist = { equipment: [], ability: [] };
  const include = (deckType, card) => {
    if (included.has(card.id)) return;
    included.add(card.id);
    shortlist[deckType].push(card);
  };
  DECK_TYPES.forEach((deckType) => {
    ranked[deckType]
      .slice(0, SHORTLIST_PER_TYPE)
      .forEach((card) => include(deckType, card));
  });
  BODY_SLOTS.forEach((slot) => {
    ranked.equipment
      .filter((card) => resolveCardBoardSlot(card) === slot)
      .slice(0, 4)
      .forEach((card) => include("equipment", card));
  });
  QUEST_EQUIPMENT_SLOTS.forEach((slot) => {
    ranked.equipment
      .filter((card) => cache.traits(card).questSlot === slot)
      .slice(0, QUEST_SHORTLIST_PER_SLOT)
      .forEach((card) => include("equipment", card));
  });
  DECK_TYPES.forEach((deckType) => {
    ranked[deckType]
      .filter((card) => cache.traits(card).supportRoles.length)
      .slice(0, SUPPORT_SHORTLIST_PER_TYPE)
      .forEach((card) => include(deckType, card));
  });

  const actives = [
    ...context.actives,
    ...shortlist.ability.filter((card) => card.kind === "active"),
  ];
  const augments = [
    ...context.augments,
    ...shortlist.ability.filter((card) => card.kind === "augment"),
  ];
  const equipment = [...context.equipmentCards, ...shortlist.equipment];
  const linkedToActives = (card) =>
    cache
      .traits(card)
      .linkPassives.some((_, effectIndex) =>
        actives.some((active) => cache.linkMatches(card, effectIndex, active)),
      );
  const abilityComplements = ranked.ability.filter((card) => {
    if (included.has(card.id)) return false;
    if (card.kind === "augment") {
      return actives.some((active) => cache.augmentMatches(card, active));
    }
    if (card.kind !== "active") return false;
    return (
      augments.some((augment) => cache.augmentMatches(augment, card)) ||
      equipment.some((entry) =>
        cache
          .traits(entry)
          .linkPassives.some((_, effectIndex) =>
            cache.linkMatches(entry, effectIndex, card),
          ),
      )
    );
  });
  const equipmentComplements = ranked.equipment.filter(
    (card) => !included.has(card.id) && linkedToActives(card),
  );
  abilityComplements
    .slice(0, SHORTLIST_COMPLEMENTS)
    .forEach((card) => include("ability", card));
  equipmentComplements
    .slice(0, SHORTLIST_COMPLEMENTS)
    .forEach((card) => include("equipment", card));
  return shortlist;
}

function runTrial(state, random) {
  const { removalPool, pools, baseline, getCard, cache, maxPower } = state;
  const span = RECOMMEND_MAX_SWAPS - RECOMMEND_MIN_SWAPS + 1;
  const target = Math.min(
    removalPool.length,
    RECOMMEND_MIN_SWAPS + Math.floor(clampRandom(random) * span),
  );
  if (target < RECOMMEND_MIN_SWAPS) return null;
  const available = [...removalPool];
  const chosen = [];
  while (chosen.length < target && available.length) {
    const index = pickWeightedIndex(
      available.map((entry) => entry.weight),
      random,
    );
    chosen.push(available.splice(index, 1)[0]);
  }
  const working = {
    equipment: [...baseline.equipment],
    ability: [...baseline.ability],
  };
  const removedIds = new Set(chosen.map((entry) => entry.cardId));
  chosen.forEach((entry) => {
    working[entry.deckType][entry.index] = null;
  });

  const addedIds = new Set();
  for (const slot of shuffle(chosen, random)) {
    const context = buildFitContext(
      filledIds(working.equipment).map(getCard),
      filledIds(working.ability).map(getCard),
      cache,
      getCard,
    );
    const counts = countIds(working[slot.deckType]);
    const usedPower = deckPower(
      working.equipment,
      working.ability,
      state.aspectId,
      state.catalogMap,
    );
    const scored = [];
    pools[slot.deckType].forEach((card) => {
      if (removedIds.has(card.id)) return;
      if ((counts[card.id] || 0) >= RECOMMEND_MAX_COPIES) return;
      if (usedPower + getCardPower(card) > maxPower) return;
      const fit = scoreCardFit(card, slot.deckType, context);
      if (fit.incompatible) return;
      scored.push({
        card,
        score:
          fit.score +
          (state.baselineCounts[card.id] ? 0 : NOVELTY_BONUS) -
          (addedIds.has(card.id) ? REPEAT_ADD_PENALTY : 0),
      });
    });
    scored.sort((a, b) => b.score - a.score || a.card.id.localeCompare(b.card.id));
    let ranked = scored;
    if (slot.deckType === "equipment") {
      const fillsBoard = scored.filter((entry) =>
        fillsMissingEquipmentSlot(entry.card, context.profile),
      );
      const fillsQuest = scored.filter((entry) =>
        fillsMissingQuestSlot(entry.card, context.profile),
      );
      if (fillsBoard.length) ranked = fillsBoard;
      else if (fillsQuest.length) ranked = fillsQuest;
    }
    const top = ranked.slice(0, CANDIDATE_TOP);
    if (!top.length) return null;
    const pick =
      top[pickWeightedIndex(top.map((_, rank) => CANDIDATE_TOP - rank), random)];
    working[slot.deckType][slot.index] = pick.card.id;
    addedIds.add(pick.card.id);
  }
  return assessProposal(state, working, chosen);
}

function recommendDeckChanges({
  equipmentIds,
  abilityIds,
  aspectId,
  catalog,
  collection,
  isOwned,
  random = Math.random,
  excludeSignatures = [],
  maxPower = RECOMMEND_MAX_POWER,
  trials = SEARCH_TRIALS,
} = {}) {
  const catalogMap = toCatalogMap(catalog);
  const owns = ownershipCheck({ collection, isOwned });
  const baseline = {
    equipment: Array.isArray(equipmentIds) ? [...equipmentIds] : [],
    ability: Array.isArray(abilityIds) ? [...abilityIds] : [],
  };
  const baseValidation = validateRecommendedDeck({
    equipmentIds: baseline.equipment,
    abilityIds: baseline.ability,
    aspectId,
    catalog: catalogMap,
    isOwned: owns,
    maxPower,
  });
  if (!baseValidation.ok) return null;

  const getCard = (cardId) => catalogMap[cardId] || null;
  const cache = createCardCache(getCard(aspectId));
  const baseEquipmentCards = baseline.equipment.map(getCard);
  const baseAbilityCards = baseline.ability.map(getCard);
  const baseEvaluation = evaluateDeck(baseEquipmentCards, baseAbilityCards, cache);
  const eligible = { equipment: [], ability: [] };
  Object.values(catalogMap)
    .filter(
      (card) =>
        card &&
        typeof card.id === "string" &&
        !isTokenCard(card) &&
        owns(card.id) &&
        cache.affinity(card).compatible,
    )
    .sort((a, b) => a.id.localeCompare(b.id))
    .forEach((card) => {
      DECK_TYPES.forEach((deckType) => {
        if (cardHasType(card, deckType)) eligible[deckType].push(card);
      });
    });
  const baselineCounts = countIds([...baseline.equipment, ...baseline.ability]);
  const pools = buildShortlist(
    eligible,
    buildFitContext(baseEquipmentCards, baseAbilityCards, cache, getCard),
    baselineCounts,
  );

  const state = {
    baseline,
    getCard,
    cache,
    catalogMap,
    owns,
    maxPower,
    aspectId,
    pools,
    baseEvaluation,
    baselineCounts,
    baselineContext: {
      cache,
      equipmentCards: baseEquipmentCards,
      abilityCards: baseAbilityCards,
      supply: baseEvaluation.supply,
      supportCounts: baseEvaluation.supportCounts,
      expensiveAbilities: baseEvaluation.expensiveAbilities,
    },
  };
  state.removalPool = buildRemovalPool(state);

  const excluded = new Set(excludeSignatures || []);
  const found = new Map();
  const trialCount = Math.max(1, Math.floor(Number(trials) || SEARCH_TRIALS));
  for (let trial = 0; trial < trialCount; trial += 1) {
    const proposal = runTrial(state, random);
    if (!proposal) continue;
    if (excluded.has(proposal.signature) || found.has(proposal.signature)) continue;
    found.set(proposal.signature, proposal);
  }
  if (!found.size) return null;

  const ranked = [...found.values()].sort(
    (a, b) => b.rank - a.rank || a.signature.localeCompare(b.signature),
  );
  const best = ranked[0].rank;
  const band = ranked
    .filter((entry) => entry.rank >= best - Math.max(3, Math.abs(best) * 0.25))
    .slice(0, PROPOSAL_BAND);
  const pick = band[Math.floor(clampRandom(random) * band.length)];
  return {
    aspectId,
    equipmentIds: pick.equipmentIds,
    abilityIds: pick.abilityIds,
    swaps: pick.swaps,
    swapCount: pick.swaps.length,
    powerBefore: baseValidation.power,
    powerAfter: pick.power,
    signature: pick.signature,
  };
}

function applyRecommendationToDraft(draft, proposal) {
  if (!draft || !proposal || !Array.isArray(proposal.swaps)) return null;
  if (!proposal.swaps.length) return null;
  const next = {
    equipment: [...(draft.equipment || [])],
    ability: [...(draft.ability || [])],
    equipmentFoils: [...(draft.equipmentFoils || [])],
    abilityFoils: [...(draft.abilityFoils || [])],
  };
  while (next.equipmentFoils.length < next.equipment.length) {
    next.equipmentFoils.push(null);
  }
  while (next.abilityFoils.length < next.ability.length) {
    next.abilityFoils.push(null);
  }
  for (const swap of proposal.swaps) {
    if (!DECK_TYPES.includes(swap?.deckType)) return null;
    const ids = swap.deckType === "equipment" ? next.equipment : next.ability;
    const foils =
      swap.deckType === "equipment" ? next.equipmentFoils : next.abilityFoils;
    if (ids[swap.index] !== swap.removedId) return null;
    ids[swap.index] = swap.addedId;
    foils[swap.index] = null;
  }
  const matches = (ids, expected) =>
    !Array.isArray(expected) ||
    (ids.length === expected.length &&
      ids.every((cardId, index) => cardId === expected[index]));
  if (
    !matches(next.equipment, proposal.equipmentIds) ||
    !matches(next.ability, proposal.abilityIds)
  ) {
    return null;
  }
  return next;
}

function hashString(text) {
  let hash = 5381;
  for (let index = 0; index < text.length; index += 1) {
    hash = ((hash << 5) + hash + text.charCodeAt(index)) | 0;
  }
  return `${(hash >>> 0).toString(36)}:${text.length}`;
}

function catalogFingerprint(catalog) {
  const cards = Array.isArray(catalog) ? catalog : Object.values(catalog || {});
  return hashString(JSON.stringify(cards));
}

function ownershipFingerprint(collection) {
  const owned = Object.keys(collection || {})
    .filter((cardId) => Number(collection[cardId]) > 0)
    .sort();
  return hashString(owned.join(","));
}

function buildRecommendationKey({
  equipment,
  ability,
  aspectId,
  catalogKey,
  ownershipKey,
} = {}) {
  return JSON.stringify([
    (equipment || []).map((cardId) => cardId || null),
    (ability || []).map((cardId) => cardId || null),
    aspectId || null,
    catalogKey || null,
    ownershipKey || null,
  ]);
}

function createSeededRandom(seed) {
  let state = Math.floor(Number(seed) || 0) >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

export {
  RECOMMEND_MIN_SWAPS,
  RECOMMEND_MAX_SWAPS,
  augmentMatches,
  aspectEffectsForCard,
  recommendDeckChanges,
  validateRecommendedDeck,
  getDeckRecommendBlocker,
  countDeckCardChanges,
  proposalSignature,
  applyRecommendationToDraft,
  catalogFingerprint,
  ownershipFingerprint,
  buildRecommendationKey,
  createSeededRandom,
};
